<template>
  <article>
    <h1 class="text-h3" id="intro" v-intersect="onIntersect">Predictions</h1>
    <p>Predictions are machine-learning models applied to datasets</p>
    <p>
      Predictions are created by processing datasets through machine-learning models into a format that is suitable for a particular application. Predictions
      are created because the computing power required to process a large volume of data can significantly impact the usability of an application. In order to
      ensure a good user experience for an application, predictions are generated offline.
    </p>

    <h2 class="text-h4" id="predictionCreate" v-intersect="onIntersect">Generating predictions</h2>
    <p>
      To generate predictions, click <v-icon>mdi-finance</v-icon> in the navigation drawer to navigate to <code>PREDICTIONS</code>. 
      Click <v-icon>mdi-plus</v-icon> to enter the prediction generation view.
    </p>
    <p>Prediction generation is a three-step process in which you gradually specify the required and optional details.</p>

    <h3 class="text-h5" id="setup" v-intersect="onIntersect">Initial setup</h3>
    <v-card flat outlined color="grey"><v-img eager contain src="@/assets/images/prediction_generate_setup.png"></v-img></v-card>

    <ol>
      <li>
        Select an application for which you wish to generate a prediction. The list only contains applications that are tagged for offline processing and for
        which you have access.
      </li>
      <li>
        Select from a number of available methods &mdash; machine-learning models that have specific requirements and outputs. You can select more than one
        method for output. The list only contains methods corresponding to the selected application.
      </li>
      <li>Select the source dataset for the application.</li>
      <li>
        Specify the number of months of decline to generate. This defaults to 600 months or 50 years, but can be lowered. A lower number generates a smaller
        output that can be processed faster.
      </li>
    </ol>

    <v-alert type="info" text border="left">Applications that do not require predictions will not appear in the application list.</v-alert>
    <v-alert type="info" text border="left">
      <strong>Note:</strong>
      Each method and application has its own specific column requirements.
    </v-alert>

    <p>
      The prediction generation process can take anywhere from one minute to a few hours <v-icon>mdi-alert</v-icon>
      depending upon the chosen application, methods, and even the size of the dataset. The process runs in the background, and notifies the user whenever it
      comepletes.
    </p>

    <h3 class="text-h5" id="columns" v-intersect="onIntersect">Data columns</h3>
    <v-card flat outlined color="grey"><v-img eager contain src="@/assets/images/prediction_generate_columns.png"></v-img></v-card>

    <p>
      Map all required data to those available in your dataset. Items marked with <strong class="red--text">*</strong> are required
    </p>
    <p>
      In order to analyse a prediction, you need to be able to filter your data. Add as many filters as you want here. Some columns are available as filters by
      default.
    </p>

    <h3 class="text-h5" id="mapping" v-intersect="onIntersect">Feature mapping</h3>
    <v-card flat outlined color="grey"><v-img eager contain src="@/assets/images/prediction_generate_features.png"></v-img></v-card>
    <p>
      In feature mapping, identify and mark each of the data columns required by the models. All of these fields are optional, and in their absence the models
      use only production history to predict the next 12 months.
    </p>

    <p>
      Click <code class="green white--text">GENERATE</code> to start the prediction generation process.
    </p>

    <h2 class="text-h4" id="predictionList" v-intersect="onIntersect">Browsing predictions</h2>
    <p>
      A complete list of predictions you have generated is available from the <code><v-icon>mdi-finance</v-icon>PREDICTIONS</code> menu.
    </p>
    <v-card flat outlined color="grey"><v-img eager contain src="@/assets/images/prediction_list.png"></v-img></v-card>

    <h2 class="text-h4" id="predictionShow" v-intersect="onIntersect">Viewing predictions</h2>
    <p>
      Predictions can be viewed as objects themselves by navigating to <code>Predictions</code>
      and selecting one of the listed items. Predictions themselves may not be intelligible as they may contain data that can only be understood when viewed
      through an application.
    </p>
    <v-card flat outlined color="grey"><v-img eager contain src="@/assets/images/prediction_view.png"></v-img></v-card>
    <p>
      Clicking on an item will load that prediction in the <code>VIEW PREDICTION</code>
      page. The prediction view mode provides an in-depth look at the generated predictions and its various characteristics through four different tabs...
    </p>
    <ul class="mb-4">
      <li>
        The <strong>INFO</strong> tab outlines object-level properties, such as the name and source (DATASET) of the prediction, its owner and when it 
        was first created and/or last updated.
      </li>
      <li>
        The <strong>DATA</strong>
        tab shows the actual contents of the generated prediction &mdash; first 15 rows of it anyway. This helps identify the output from various methods.
      </li>
      <li>
        The <strong>EXPORTS</strong>
        tab lists all the exports that have been generated for this predictions. Exports can be downloaded from this list.
      </li>
      <li>
        The
        <strong>DETAILS</strong>
        tab shows some pre-determined statistics of each of the data columns in the prediction. Each statistic provides some insight into the nature of the
        processed data.
      </li>
    </ul>

    <h3 class="text-h5" id="export" v-intersect="onIntersect">Export predictions</h3>
    <p>
      Predictions can either be analysed within Sky, using one of the subscribed applications, or they can be exported to any other software, or data format
      that best fits your existing workflow.
    </p>
    <p>Predictions can be exported to files or dumped into tables in a database.</p>
    <v-card flat outlined color="grey"><v-img eager contain class="mx-auto" width="600" src="@/assets/images/prediction_export.png"></v-img></v-card>
    <p>To export a prediction...</p>
    <ol>
      <li>Choose the desired <code>targets</code> and <code>methods</code></li>
      <li>Specify if you want to include <code>history</code> and <code>short-term predictions</code> as part of the declined output</li>
      <li>Determine how long you want the decline to be &mdash; defaults to a maximum of 600 months or 50 years</li>
      <li>If you want to export to a table in a database, specify the <code>connection</code> and <code>table</code> information, otherwise leave it blank.</li>
      <li>Click <code class="green white--text">Export</code>to either download a file or write the predictions to the database.</li>
    </ol>
    <v-card flat outlined>
      <v-expansion-panels flat>
        <v-expansion-panel class="info white--text">
          <v-expansion-panel-header>
            Please note that export to database functionality expects either of the following tables to already exist in the database.
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <pre>
  CREATE TABLE [dbo].[Exports](
    [BatchNum] [uniqueidentifier] NOT NULL,
    [ExportedOn] [datetime] NOT NULL,
    [ExportedBy] [nvarchar](50) NOT NULL,
    [WellIdentifier] [nvarchar](50) NOT NULL,
    [Method] [nvarchar](50) NOT NULL,
    [Type] [nvarchar](50) NOT NULL,
    [Month] [date] NOT NULL,
    [Oil] [int] NULL,
    [Gas] [int] NULL,
    [Water] [int] NULL
  ) ON [PRIMARY]
            </pre>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </v-card>

    <h2 class="text-h4" id="predictionEdit" v-intersect="onIntersect">Editing predictions</h2>
    <p>
      Editing a prediction is the same as generating one. When viewing a prediction, click <v-icon>mdi-pencil-outline</v-icon> tab to start editing.
    </p>
    <v-card flat outlined color="grey"><v-img eager contain src="@/assets/images/prediction_edit.png"></v-img></v-card>
    <p>
      The prediction updation process can take anywhere from one minute to a few hours <v-icon>mdi-alert</v-icon>
      depending upon the chosen application, methods, and even the size of the dataset. The process runs in the background, and notifies the user whenever it
      comepletes.
    </p>

    <h2 class="text-h4" id="predictionDelete" v-intersect="onIntersect">Deleting predictions</h2>
    <p>
      Predictions can be deleted from the <code>DELETE</code> page. The application asks for confirmation before deleting a prediction.
    </p>
    <v-card flat outlined color="grey"><v-img eager contain src="@/assets/images/prediction_delete.png"></v-img></v-card>
    <v-alert type="info" text>Please note that deleting a prediction will NOT affect any dataset or file.</v-alert>

    <contents :items="menu" ref="contents" />
  </article>
</template>
<script>
import Contents from "@/components/contents"
export default {
  name: "HelpPredictions",
  components: { Contents },
  data() {
    return {
      menu: [
        { id: "intro", text: "Introduction", depth: 1 },
        { id: "predictionCreate", text: "Generating", depth: 1 },
        { id: "setup", text: "Initial setup", depth: 2 },
        { id: "columns", text: "Data columns", depth: 2 },
        { id: "mapping", text: "Feature mapping", depth: 2 },
        { id: "predictionList", text: "Browsing", depth: 1 },
        { id: "predictionShow", text: "Viewing", depth: 1 },
        { id: "export", text: "Export predictions", depth: 2 },
        { id: "predictionEdit", text: "Editing", depth: 1 },
        { id: "predictionDelete", text: "Deleting", depth: 1 },
      ],
    }
  },
}
</script>
